import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@gymflow/helpers";
import { DateTime } from "luxon";
import { useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";

import { Button } from "../../atoms";

export const HostedPagesDayPicker = ({
  currentDate,
  setCurrentDate,
  className,
}: {
  currentDate: DateTime;
  setCurrentDate: (date: DateTime) => void;
  className?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width = 0 } = useResizeObserver({
    ref: containerRef,
    box: "border-box",
  });
  const today = DateTime.now().setZone(currentDate.zone).startOf("day");
  const [currentFirstDate, setCurrentFirstDate] = useState(currentDate);
  const canGoBack = currentFirstDate > today;
  const amountToRender = Math.max(
    0,
    Math.floor(width / 56) - (canGoBack ? 2 : 1),
  );
  const [parent] = useAutoAnimate();
  return (
    <div
      ref={containerRef}
      className={cn("flex items-center justify-center", className)}
    >
      <div className="flex flex-row items-center gap-2" ref={parent}>
        {canGoBack && (
          <div className="flex w-12 items-center justify-center">
            <Button
              onClick={() => {
                const targetDate = currentFirstDate.minus({
                  days: amountToRender,
                });
                if (targetDate <= today.plus({ days: 1 })) {
                  setCurrentFirstDate(today);
                } else {
                  setCurrentFirstDate(targetDate);
                }
              }}
              intent="transparent"
              className="size-6 min-w-0 p-2 transition-opacity"
            >
              <FontAwesomeIcon icon={faAngleLeft} className="size-4" />
            </Button>
          </div>
        )}
        {new Array(amountToRender ?? 0).fill(0).map((_, i) => {
          const date = currentFirstDate.plus({ days: i });
          return (
            <Button
              intent={
                date.hasSame(currentDate, "day")
                  ? "secondary-outline"
                  : "default"
              }
              key={`day-${date.toISO()}`}
              className="!h-14 !w-12 flex-col !p-2 font-medium"
              onClick={() => {
                setCurrentDate(date);
              }}
            >
              <div className="text-sm">
                {date.toLocaleString({
                  weekday: "short",
                })}
              </div>
              <div className="font-semibold">
                {date.toLocaleString({
                  day: "numeric",
                })}
              </div>
            </Button>
          );
        })}
        <div className="flex w-12 items-center justify-center">
          <Button
            onClick={() => {
              setCurrentFirstDate(
                currentFirstDate.plus({ days: amountToRender }),
              );
            }}
            intent="transparent"
            className="size-6 min-w-0 p-2"
          >
            <FontAwesomeIcon icon={faAngleRight} className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
